<template>
  <v-dialog :value="value" max-width="1000" @input="close" eager>
    <v-card class="global-product-upsert">
      <v-toolbar flat color="primary" dark>
        <v-toolbar-title class="font-weight-bold">
          {{ dialogTitle }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="close" :disabled="isLoading">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="py-4 px-4">
        <v-form v-model="isFormValid" ref="form">
          <v-row dense>
            <v-col cols="12" md="2">
              <div :class="imageBoxClasses" @click="triggerFileInput">
                <input
                  ref="fileInput"
                  type="file"
                  accept="image/*"
                  class="d-none"
                  :disabled="isLoading"
                  @change="onImageChange" />
                <img v-if="hasImage" :src="imagePreview || updates.image" />
                <v-icon v-else color="primary">mdi-image-plus</v-icon>
              </div>
            </v-col>
            <v-col cols="12" md="10">
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="updates.name"
                    :label="addAsterisk($t('generalAttributes.name'))"
                    :rules="[rules.required]"
                    :disabled="isLoading"
                    dense
                    outlined />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="updates.category_id"
                    :items="categories"
                    item-text="name"
                    item-value="id"
                    :label="addAsterisk($t('entities.products.chooseCategory'))"
                    :rules="[rules.required]"
                    :disabled="isLoading || !categories.length"
                    dense
                    outlined />
                </v-col>
                <v-col cols="12" md="3">
                  <v-switch
                    v-model="updates.track_stock"
                    :label="$t('entities.products.attributes.trackStock')"
                    :disabled="isLoading || isTrackStockDisabled"
                    inset
                    dense
                    class="mt-1" />
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="updates.description"
                    :label="$t('generalAttributes.description')"
                    :disabled="isLoading"
                    rows="1"
                    outlined
                    dense
                    @blur="
                      updates.description = updates.description?.trim() || null
                    " />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <div class="variants-section">
        <h4 class="mb-3 font-weight-bold text-uppercase">
          {{ $t('entities.products.productVariants') }}
        </h4>
        <p
          v-if="!variants.length"
          class="add-variants-warning text--secondary mb-0">
          {{ $t('entities.products.addVariantMessage') }}
        </p>
        <div v-else ref="variantList" class="variants-section__variant-list">
          <VariantUpsertCard
            v-for="(variant, index) in variants"
            :key="variant.uid"
            :value="variant"
            :original="getOriginalVariant(variant, index)"
            :is-disabled="isLoading"
            :is-new="!variant.id"
            :is-coins-category="isCoinsCategory"
            :is-passes-category="isPassesCategory"
            @input="updateVariant(index, $event)"
            @remove="removeVariant(index)" />
        </div>
        <div class="variants-section__actions">
          <v-btn
            color="primary"
            small
            :disabled="isLoading"
            @click="addVariant">
            {{ $t('entities.products.addVariant') }}
          </v-btn>
        </div>
      </div>
      <v-card-actions class="py-4">
        <v-btn text @click="close" :disabled="isLoading">
          {{ $t('miscellaneous.cancel') }}
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="isLoading"
          :disabled="isSubmitDisabled"
          @click="submit">
          {{ submitLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import VariantUpsertCard from './VariantUpsertCard.vue'
import FilesApi from '@/services/misc/FilesApi'
import { mapActions } from 'vuex'
import isRole from '@/mixins/crud/computed/isRole'

const resetUpdates = (product = {}) => ({
  id: product.id || '',
  name: product.name || '',
  category_id: product.category_id || null,
  sort_order: product.sort_order || 1,
  track_stock: product.track_stock || false,
  image: product.image || null,
  description: product.description || null,
})

const normalizeAttributes = (attributes = {}) => {
  if (
    !attributes ||
    typeof attributes !== 'object' ||
    Array.isArray(attributes)
  )
    return {}
  return Object.keys(attributes)
    .sort()
    .reduce((acc, key) => {
      acc[key] = attributes[key]
      return acc
    }, {})
}

const createVariantState = (variant = {}, sortOrder = 1) => ({
  id: variant.id || '',
  name: variant.name || '',
  description: variant.description || null,
  image: variant.image || null,
  sort_order: variant.sort_order || sortOrder,
  attributes: normalizeAttributes(variant.attributes),
  pack_type: variant.pack_type || null,
})

const normalizeVariantForCompare = (variant, index) => ({
  id: variant?.id || '',
  name: variant?.name ? variant.name.trim() : '',
  description: variant?.description ? variant.description.trim() : null,
  image: variant?.image ? variant.image.trim() : null,
  sort_order:
    typeof variant?.sort_order === 'number' ? variant.sort_order : index + 1,
  attributes: normalizeAttributes(variant?.attributes),
  pack_type: variant?.pack_type || null,
})

const buildVariantUid = (variant, index) =>
  `variant-${variant.id || `new-${index + 1}`}-${Date.now()}`

export default {
  name: 'GlobalProductUpsertDialog',
  mixins: [isRole],
  components: {
    VariantUpsertCard,
  },
  props: {
    categories: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
    product: { type: Object, default: () => ({}) },
    value: { type: Boolean, required: true },
  },
  data: () => ({
    updates: resetUpdates(),
    isFormValid: false,
    variants: [],
    originalVariants: [],
    deletedVariantIds: [],
    imageFile: null,
    imagePreview: '',
  }),
  computed: {
    dialogTitle() {
      return this.isEditing
        ? this.$t('entities.products.editGlobalProduct')
        : this.$t('entities.products.createGlobalProduct')
    },
    hasImage() {
      return !!this.updates.image || !!this.imagePreview
    },
    imageBoxClasses() {
      return this.hasImage ? 'image-upload has-image' : 'image-upload'
    },
    selectedCategory() {
      if (!this.updates.category_id) return null
      return this.categories.find((it) => it.id === this.updates.category_id)
    },
    isCoinsCategory() {
      return this.selectedCategory?.code === 'CNS'
    },
    isPassesCategory() {
      return this.selectedCategory?.code === 'PAS'
    },
    isDigitalAsset() {
      return this.isCoinsCategory || this.isPassesCategory
    },
    isTrackStockDisabled() {
      return !this.isAdmin
    },
    isEditing() {
      return !!this.product?.id
    },
    rules() {
      return {
        required: (v) => !!v || this.$t('validation.required'),
      }
    },
    submitLabel() {
      return this.isEditing
        ? this.$t('miscellaneous.save')
        : this.$t('miscellaneous.create')
    },
    areVariantsValidForSubmit() {
      if (!Array.isArray(this.variants) || this.variants.length === 0) {
        return false
      }
      return this.variants.every((variant, index) => {
        if (!variant) return false
        const isDirty = this.isVariantDirty(variant, index)
        if (!variant.id || isDirty) {
          return this.isVariantValid(variant)
        }
        return true
      })
    },
    isSubmitDisabled() {
      return (
        this.isLoading || !this.isFormValid || !this.areVariantsValidForSubmit
      )
    },
  },
  methods: {
    ...mapActions(['showSnackbar']),
    addVariant() {
      const nextSortOrder = this.variants.length + 1
      const base = createVariantState({}, nextSortOrder)
      const nextVariant = {
        ...base,
        uid: buildVariantUid(base, nextSortOrder - 1),
      }
      this.variants = [...this.variants, nextVariant]
      this.scrollVariantListToBottom()
    },
    close() {
      this.$emit('input', false)
    },
    getPayload() {
      const { id, image, description, ...updates } = this.updates
      updates.image = image
      updates.description = description
      return { id, updates }
    },
    onClose() {
      this.updates = resetUpdates()
      this.variants = []
      this.originalVariants = []
      this.deletedVariantIds = []
      this.resetForm()
      this.resetFileInput()
      this.resetImagePreview()
      this.imageFile = null
    },
    onImageChange(event) {
      const file = event?.target?.files?.[0]
      if (!file) {
        this.imageFile = null
        this.resetImagePreview()
        return
      }
      this.imageFile = file
      this.resetImagePreview()
      this.imagePreview = URL.createObjectURL(file)
      if (event?.target) event.target.value = ''
    },
    onOpen() {
      this.updates = resetUpdates(this.product)
      this.initializeVariants()
      this.resetForm()
      this.resetFileInput()
      this.resetImagePreview()
      this.imageFile = null
    },
    initializeVariants() {
      const initialVariants = Array.isArray(this.product?.variants)
        ? this.product.variants
        : []
      const normalized = initialVariants.map((variant, index) =>
        createVariantState(variant, index + 1),
      )
      this.originalVariants = normalized.map((variant) => ({ ...variant }))
      this.variants = normalized.map((variant, index) => ({
        ...variant,
        uid: buildVariantUid(variant, index),
      }))
    },
    getOriginalVariant(variant, index) {
      if (variant?.id) {
        const match = this.originalVariants.find(
          (item) => item.id === variant.id,
        )
        if (match) return { ...match }
      }
      return {
        sort_order:
          typeof variant?.sort_order === 'number'
            ? variant.sort_order
            : index + 1,
      }
    },
    resetFileInput() {
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    resetImagePreview() {
      if (this.imagePreview) {
        URL.revokeObjectURL(this.imagePreview)
      }
      this.imagePreview = ''
    },
    resetForm() {
      if (this.$refs.form) this.$refs.form.resetValidation()
      this.isFormValid = false
    },
    async submit() {
      if (!this.$refs.form || !this.$refs.form.validate()) return
      if (this.imageFile) {
        const uploaded = await this.uploadSelectedImage()
        if (!uploaded) return
      }
      const payload = this.getPayload()
      const variants = this.buildVariantPayloads()
      this.$emit('product:upsert', {
        ...payload,
        variants,
        deletedVariantIds: [...this.deletedVariantIds],
      })
    },
    async uploadSelectedImage() {
      try {
        const response = await FilesApi.upload('arenas', this.imageFile)
        this.imageFile = null
        if (!response || response.status !== 200 || !response.data?.link) {
          this.notifyUploadError()
          return false
        }
        this.updates.image = response.data.link
        this.resetImagePreview()
        return true
      } catch (error) {
        console.error(error)
        this.notifyUploadError()
        return false
      }
    },
    notifyUploadError() {
      this.showSnackbar({
        color: 'error',
        message: this.$t('miscellaneous.fileUploadFailed'),
      })
    },
    triggerFileInput() {
      if (this.isLoading) return
      this.$refs.fileInput?.click()
    },
    updateVariant(index, value) {
      const current = this.variants[index]
      if (!current) return
      this.$set(this.variants, index, {
        ...current,
        ...value,
        sort_order: index + 1,
        uid: current.uid,
      })
    },
    scrollVariantListToBottom() {
      this.$nextTick(() => {
        const list = this.$refs.variantList
        if (!list) return
        if (typeof list.scrollTo === 'function') {
          list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' })
        } else {
          list.scrollTop = list.scrollHeight
        }
      })
    },
    isVariantDirty(variant, index) {
      if (!variant?.id) return true
      const original = this.originalVariants.find(
        (item) => item.id === variant.id,
      )
      if (!original) return true
      const currentNormalized = normalizeVariantForCompare(variant, index)
      const originalNormalized = normalizeVariantForCompare(original, index)
      return (
        JSON.stringify(currentNormalized) !== JSON.stringify(originalNormalized)
      )
    },
    isVariantValid(variant) {
      const name = variant?.name ? variant.name.trim() : ''
      if (!name) return false
      if (this.isCoinsCategory) {
        const type = variant?.attributes?.type
        if (!type) return false
        if (type !== 'coins') {
          const amount = variant?.attributes?.amount
          if (amount === '' || amount === null || amount === undefined)
            return false
          const parsed = Number(amount)
          if (!Number.isInteger(parsed)) return false
        }
      }
      if (this.isPassesCategory) {
        const validFrom = variant?.attributes?.valid_from
        const validTo = variant?.attributes?.valid_to
        const weekdays = variant?.attributes?.weekdays
        if (!validFrom || !validTo) return false
        if (!Array.isArray(weekdays) || weekdays.length === 0) return false
        const allValid = weekdays.every((day) => {
          const parsed = Number(day)
          return Number.isInteger(parsed) && parsed >= 0 && parsed <= 6
        })
        if (!allValid) return false
      }
      return true
    },
    removeVariant(index) {
      if (index < 0 || index >= this.variants.length) return
      const variant = this.variants[index]
      if (variant?.id) {
        this.deletedVariantIds.push(variant.id)
      }
      const next = [...this.variants]
      next.splice(index, 1)
      this.variants = next.map((v, idx) => ({
        ...v,
        sort_order: idx + 1,
      }))
    },
    buildVariantPayloads() {
      if (!Array.isArray(this.variants)) return []
      const originalsById = this.originalVariants.reduce((acc, variant) => {
        if (variant.id) acc[variant.id] = variant
        return acc
      }, {})
      return this.variants
        .map((variant, index) => {
          if (!variant) return null
          const payload = this.normalizeVariantPayload(variant, index)
          if (!payload) return null
          if (variant.id) {
            const original = originalsById[variant.id]
            if (original) {
              const originalPayload = this.normalizeVariantPayload(
                original,
                index,
              )
              if (this.areVariantPayloadsEqual(payload, originalPayload)) {
                return null
              }
            }
            return { id: variant.id, ...payload }
          }
          return payload
        })
        .filter(Boolean)
    },
    normalizeVariantPayload(variant, index) {
      const name = variant?.name ? variant.name.trim() : ''
      if (!name) return null
      const description = variant?.description
        ? variant.description.trim()
        : null
      const image = variant?.image ? variant.image.trim() : null
      const attributes = normalizeAttributes(variant?.attributes)
      const sortOrder =
        typeof variant?.sort_order === 'number' ? variant.sort_order : index + 1
      const payload = {
        name,
        sort_order: sortOrder,
        pack_type: variant?.pack_type || null,
        attributes,
      }
      payload.description = description
      payload.image = image
      return payload
    },
    areVariantPayloadsEqual(current, original) {
      if (!current || !original) return false
      return JSON.stringify(current) === JSON.stringify(original)
    },
    addAsterisk(label) {
      return `${label} *`
    },
  },
  watch: {
    value(val) {
      return val ? this.onOpen() : this.onClose()
    },
    isDigitalAsset(isDigital) {
      if (isDigital) {
        this.updates.track_stock = false
      }
    },
  },
}
</script>

<style scoped>
.global-product-upsert {
  --variant-section-padding: 0.5rem;
  --variant-section-background-color: #f5f5f5;
}

.image-upload {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #bdbdbd;
  border-radius: 8px;
  height: 7.625rem;
  cursor: pointer;
  overflow: hidden;
}

.image-upload.has-image {
  border: none;
  height: 7.625rem;
}

.image-upload img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.variants-section {
  border-radius: 4px;
  margin: 0 1rem;
  padding: var(--variant-section-padding) var(--variant-section-padding) 0;
  background-color: var(--variant-section-background-color);
}

.add-variants-warning {
  padding: 0.5rem 0 0.75rem;
  text-align: center;
}

.variants-section__actions {
  display: flex;
  justify-content: center;
  margin: 0 1rem;
  padding: var(--variant-section-padding) 0;
  background-color: var(--variant-section-background-color);
}

.variants-section__variant-list {
  max-height: 15rem;
  overflow-y: auto;
}

.variants-section__variant-list ::v-deep .variant-card:last-child {
  margin-bottom: 0 !important;
}
</style>
